import React, { useEffect, useState } from 'react'
import Confetti from 'react-confetti'
import {
  Typography,
  Skeleton,
  Button,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { useGetTickets, usePostWinners } from '../../api/lucky-draw'
import { useSnackbar } from 'notistack'

// Constants for spin speed control
const INITIAL_DELAY = 100
const SPEED_UP_FACTOR = 0.95
const SLOW_DOWN_FACTOR = 1.09
const MAX_DELAY = 300
const MIN_DELAY = 20

const WinnerPicker = () => {
  const { data: pointData = [], isLoading } = useGetTickets()
  const { mutateAsync } = usePostWinners()
  const [filteredCompany, setFilteredCompany] = useState([])
  const [name, setName] = useState('Lucky Draw')
  const [winners, setWinners] = useState([])
  const { enqueueSnackbar } = useSnackbar()
  const [isRendering, setIsRendering] = useState(false)
  const [isSlowingDown, setIsSlowingDown] = useState(false)
  const [isSavingWinners, setIsSavingWinners] = useState(false)

  // Handle spinning effect
  useEffect(() => {
    if (!isRendering) return

    let delay = INITIAL_DELAY
    let spinActive = true

    const spinOnce = () => {
      if (!spinActive || filteredCompany.length === 0) return

      const randomIndex = Math.floor(Math.random() * filteredCompany.length)
      setName(filteredCompany[randomIndex])

      if (isSlowingDown) {
        delay *= SLOW_DOWN_FACTOR
        if (delay > MAX_DELAY) {
          spinActive = false
          setIsRendering(false)
          setIsSlowingDown(false)
          return
        }
      } else {
        delay = Math.max(MIN_DELAY, delay * SPEED_UP_FACTOR)
      }

      setTimeout(spinOnce, delay)
    }

    spinOnce()

    return () => {
      spinActive = false
    }
  }, [isRendering, isSlowingDown, filteredCompany])

  // Handle winner selection after spin stops
  useEffect(() => {
    if (isRendering || name === 'Lucky Draw' || filteredCompany.length === 0) return

    setWinners((prev) => [...prev, name])

    setFilteredCompany((prev) => {
      const updated = [...prev]
      const index = updated.indexOf(name)
      if (index !== -1) updated.splice(index, 1)
      return updated
    })
  }, [name, isRendering])

  // Initialize company list from API
  useEffect(() => {
    if (!isLoading && pointData) setFilteredCompany(pointData)
  }, [pointData, isLoading])

  const handleTypographyClick = () => {
    navigator.clipboard
      .writeText(name)
      .then(() => {
        enqueueSnackbar(`Successfully copied`, { variant: 'success' })
      })
      .catch(() => {
        enqueueSnackbar(`Failed to copy`, { variant: 'error' })
      })
  }

  const handleSaveWinners = async () => {
    setIsSavingWinners(true)
    try {
      await mutateAsync({ winners })
      enqueueSnackbar('Winners list saved! Page will refresh in 3 seconds...', {
        variant: 'success',
      })
      setTimeout(() => window.location.reload(), 1000 * 3)
    } catch {
      setIsSavingWinners(false)
      enqueueSnackbar('Something went wrong. Please try again.', { variant: 'error' })
    }
  }

  return (
    <Box>
      <Confetti height={'1000px'} />
      <Grid container alignItems='center' marginTop={5} spacing={2}>
        <Grid item xs={12}>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            sx={{ border: '2px solid black', cursor: 'pointer', padding: 2 }}
            onClick={handleTypographyClick}
          >
            <Typography
              sx={{
                fontSize: '30px',
                textTransform: 'uppercase',
                transition: '0.5s all',
              }}
            >
              {isLoading ? <Skeleton style={{ width: '100%' }} /> : name}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={4} />
        <Grid item xs={4}>
          {!isLoading && (
            <Button
              variant='contained'
              onClick={() => (isRendering ? setIsSlowingDown(true) : setIsRendering(true))}
              disabled={isSlowingDown || isSavingWinners}
              sx={{
                backgroundColor: isRendering ? 'red' : 'green',
                '&:hover': {
                  backgroundColor: isRendering ? 'red' : 'green',
                },
                height: '48px',
                width: '400px',
              }}
            >
              {isRendering && !isSlowingDown ? 'STOP' : 'START'}
            </Button>
          )}
        </Grid>
        <Grid item xs={4} />
      </Grid>
      {winners.length > 0 && (
        <Box mt={4}>
          <Typography variant='h6'>Winners List</Typography>
          <Table>
            <TableBody>
              {winners.map((winner, index) => (
                <TableRow key={index}>
                  <TableCell style={{ textTransform: 'uppercase' }}>{winner}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Grid container alignItems='center' marginTop={5} spacing={2}>
        <Grid item xs={4} />
        <Grid item xs={4}>
          {winners.length > 0 && (
            <Button
              variant='outlined'
              onClick={handleSaveWinners}
              sx={{ height: '48px', width: '100%' }}
              disabled={isSavingWinners}
            >
              Save Winners
            </Button>
          )}
        </Grid>
        <Grid item xs={4} />
      </Grid>
    </Box>
  )
}

export default WinnerPicker
